<!-- ModalDetalleDiseno.vue — Detalle y notificación de una preaprobación -->
<!--
  Es la pantalla donde Ventas dispara el WhatsApp y donde consulta qué
  respondió el cliente en cada documento. Notificar es irreversible: a partir
  de ahí el registro queda congelado como evidencia.
-->
<template>
  <div class="modal fade" id="modalDetalleDiseno" tabindex="-1" ref="el">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content modal-divisan border-0">
        <!-- Header -->
        <div class="modal-header border-0 pb-2">
          <div class="header-info">
            <div class="header-cliente">
              <span class="badge-cliente">{{ diseno?.cliente_numero }}</span>
              <span class="cliente-nombre">{{ diseno?.cliente }}</span>
            </div>
            <h5 class="header-titulo">Cotización {{ diseno?.cotizacion }}</h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :disabled="notificando"
          ></button>
        </div>

        <div class="modal-body pt-2">
          <!-- Resumen -->
          <div class="resumen">
            <div class="resumen-item">
              <span class="resumen-label">Estatus</span>
              <span :class="badgeEstatus(diseno?.estatus)">
                {{ labelEstatus(diseno?.estatus) }}
              </span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">WhatsApp</span>
              <span :class="badgeWhatsapp(diseno?.estatus_whatsapp)">
                <i class="bi bi-whatsapp me-1"></i>
                {{ labelWhatsapp(diseno?.estatus_whatsapp) }}
              </span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Creación</span>
              <span class="resumen-valor">{{ formatFecha(diseno?.creado_en) }}</span>
            </div>
            <div class="resumen-item">
              <span class="resumen-label">Notificado</span>
              <span class="resumen-valor">{{ formatFecha(diseno?.notificado_en) }}</span>
            </div>
          </div>

          <!-- Notificar -->
          <div class="bloque-notificar">
            <div class="notificar-texto">
              <template v-if="yaNotificado">
                <i class="bi bi-check-circle-fill text-success me-1"></i>
                Ya se le mandó el link al cliente
                <span v-if="diseno?.creado_por_nombre" class="autor">
                  · registro creado por {{ diseno.creado_por_nombre }}
                </span>
              </template>
              <template v-else-if="!tieneTelefono">
                <i class="bi bi-exclamation-triangle-fill text-danger me-1"></i>
                El cliente no tiene teléfono registrado.
              </template>
              <template v-else>
                <i class="bi bi-info-circle me-1"></i>
                Al notificar, el registro se congela y ya no podrá editarse.
              </template>
            </div>

            <button
              class="btn-notificar"
              :disabled="!puedeNotificar"
              :title="motivoBloqueo"
              @click="confirmarNotificar"
            >
              <span v-if="notificando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-whatsapp me-1"></i>
              {{ notificando ? "Enviando..." : "Notificar cliente" }}
            </button>
          </div>

          <!-- Link público, por si hay que reenviarlo a mano -->
          <div v-if="diseno?.link_publico" class="link-box">
            <span class="link-label">Link del cliente</span>
            <code class="link-valor">{{ diseno.link_publico }}</code>
            <button class="btn-copiar" :title="copiado ? 'Copiado' : 'Copiar'" @click="copiarLink">
              <i class="bi" :class="copiado ? 'bi-check-lg' : 'bi-clipboard'"></i>
            </button>
          </div>

          <!-- Documentos -->
          <h6 class="seccion-titulo">
            <i class="bi bi-files me-1"></i>
            Documentos ({{ diseno?.pdfs?.length || 0 }})
          </h6>

          <div v-if="!diseno?.pdfs?.length" class="vacio">Sin documentos adjuntos.</div>

          <div v-for="pdf in diseno?.pdfs || []" :key="pdf.id" class="pdf-card">
            <div class="pdf-header">
              <div class="pdf-info">
                <i class="bi bi-file-earmark-pdf-fill text-danger"></i>
                <span class="pdf-nombre">{{ pdf.nombre_original }}</span>
                <span :class="badgePdf(pdf.estatus)">{{ labelPdf(pdf.estatus) }}</span>
              </div>
              <a :href="pdf.archivo" target="_blank" rel="noopener" class="btn-visualizar">
                <i class="bi bi-eye me-1"></i>Visualizar
              </a>
            </div>

            <!-- El motivo del rechazo es el dato accionable: dice qué corregir -->
            <div v-if="pdf.comentario_rechazo" class="comentario">
              <i class="bi bi-chat-left-quote-fill me-2"></i>
              <div>
                <strong>Comentario del cliente</strong>
                <span class="d-block">{{ pdf.comentario_rechazo }}</span>
                <small v-if="pdf.respondido_en" class="respondido">
                  {{ formatFecha(pdf.respondido_en) }}
                </small>
              </div>
            </div>
            <small v-else-if="pdf.respondido_en" class="respondido-solo">
              Respondido el {{ formatFecha(pdf.respondido_en) }}
            </small>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-cerrar" data-bs-dismiss="modal" :disabled="notificando">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { notificarDiseno } from "@/utils/api_backend.js";

const emit = defineEmits(["notificado"]);

const el = ref(null);
let modalInstance = null;

const diseno = ref(null);
const notificando = ref(false);
const copiado = ref(false);

const yaNotificado = computed(() => diseno.value?.estatus_whatsapp !== "NO_ENVIADO");
const tieneTelefono = computed(() => !!diseno.value?.cliente_telefono);
const tienePdfs = computed(() => (diseno.value?.pdfs?.length || 0) > 0);

const puedeNotificar = computed(
  () => !yaNotificado.value && tieneTelefono.value && tienePdfs.value && !notificando.value
);

const motivoBloqueo = computed(() => {
  if (yaNotificado.value) return "Ya se notificó a este cliente";
  if (!tieneTelefono.value) return "El cliente no tiene teléfono registrado";
  if (!tienePdfs.value) return "El registro no tiene documentos";
  return "Mandar el link de revisión por WhatsApp";
});

onMounted(() => {
  modalInstance = new Modal(el.value);
});

function abrir(registro) {
  diseno.value = registro;
  notificando.value = false;
  copiado.value = false;
  modalInstance.show();
}
defineExpose({ abrir });

// ── Notificar ──────────────────────────────────────────────
async function confirmarNotificar() {
  const { isConfirmed } = await Swal.fire({
    icon: "question",
    title: "¿Notificar al cliente?",
    text: "El registro ya no podrá editarse.",
    showCancelButton: true,
    confirmButtonText: "Sí, notificar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#25d366",
  });
  if (!isConfirmed) return;

  notificando.value = true;
  try {
    await notificarDiseno(diseno.value.id);
    // El backend ya marcó ENVIADO; se refleja de una vez para que el botón no
    // quede habilitado y se mande dos veces. Se muta la propiedad en vez de
    // reemplazar el objeto: es el mismo registro que tiene la tabla, y
    // conservar la referencia deja que los avisos del WebSocket lleguen aquí.
    diseno.value.estatus_whatsapp = "ENVIADO";
    emit("notificado");
    Swal.fire({
      icon: "success",
      title: "Notificación enviada",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    Swal.fire(
      "Error",
      err.response?.data?.detail || "No se pudo enviar la notificación.",
      "error"
    );
  } finally {
    notificando.value = false;
  }
}

async function copiarLink() {
  try {
    await navigator.clipboard.writeText(diseno.value.link_publico);
    copiado.value = true;
    setTimeout(() => (copiado.value = false), 1800);
  } catch {
    Swal.fire("Copia manual", diseno.value.link_publico, "info");
  }
}

// ── Presentación ───────────────────────────────────────────
const ESTATUS = {
  CREADO: { label: "Creado", clase: "estatus-creado" },
  EN_PROCESO: { label: "En proceso", clase: "estatus-proceso" },
  APROBADO: { label: "Aprobado", clase: "estatus-aprobado" },
  RECHAZADO: { label: "Rechazado", clase: "estatus-rechazado" },
};
const WHATSAPP = {
  NO_ENVIADO: { label: "No enviado", clase: "wa-no" },
  ENVIADO: { label: "Enviado", clase: "wa-enviado" },
  ENTREGADO: { label: "Entregado", clase: "wa-entregado" },
  LEIDO: { label: "Leído", clase: "wa-leido" },
  FALLIDO: { label: "Falló", clase: "wa-fallido" },
};
const PDF = {
  PENDIENTE: { label: "Pendiente", clase: "pdf-pendiente" },
  APROBADO: { label: "Aprobado", clase: "pdf-aprobado" },
  RECHAZADO: { label: "Rechazado", clase: "pdf-rechazado" },
};

const labelEstatus = (v) => ESTATUS[v]?.label || v || "—";
const badgeEstatus = (v) => `badge-pill ${ESTATUS[v]?.clase || ""}`;
const labelWhatsapp = (v) => WHATSAPP[v]?.label || v || "—";
const badgeWhatsapp = (v) => `badge-pill ${WHATSAPP[v]?.clase || ""}`;
const labelPdf = (v) => PDF[v]?.label || v;
const badgePdf = (v) => `badge-pill ${PDF[v]?.clase || ""}`;

function formatFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
</script>

<style scoped>
.modal-divisan {
  background-color: var(--d-cream);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  border-radius: 18px;
}
.modal-header {
  padding: 1.1rem 1.4rem 0.8rem;
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.12) !important;
}
.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.header-cliente {
  display: flex;
  align-items: center;
  gap: 7px;
}
.badge-cliente {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-family: var(--d-font-mono);
  font-size: 0.73rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 7px;
}
.cliente-nombre {
  font-size: 0.82rem;
  color: var(--d-green);
  font-weight: 600;
}
.header-titulo {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--d-brown);
  margin: 0;
}
.modal-body {
  padding: 1rem 1.4rem;
}

/* ── Resumen ────────────────────────────────────────────── */
.resumen {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  padding-bottom: 0.9rem;
  border-bottom: 0.5px solid rgba(64, 34, 8, 0.12);
  margin-bottom: 0.9rem;
}
.resumen-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.resumen-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--d-green);
}
.resumen-valor {
  font-size: 0.8rem;
  color: var(--d-brown);
}

.badge-pill {
  display: inline-block;
  width: fit-content;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 1px 10px;
}
.estatus-creado,
.wa-no {
  background: rgba(64, 34, 8, 0.1);
  color: #6d4c41;
}
.estatus-proceso,
.wa-enviado {
  background: #e3f2fd;
  color: #1565c0;
}
.estatus-aprobado,
.wa-entregado,
.wa-leido,
.pdf-aprobado {
  background: #e8f5e9;
  color: #1b5e20;
}
.estatus-rechazado,
.pdf-rechazado {
  background: #fdeaea;
  color: #c62828;
}
.wa-fallido {
  background: rgba(198, 40, 40, 0.12);
  color: #c62828;
}
.pdf-pendiente {
  background: rgba(64, 34, 8, 0.1);
  color: #6d4c41;
}

/* ── Notificar ──────────────────────────────────────────── */
.bloque-notificar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.22);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 10px;
}
.notificar-texto {
  font-size: 0.79rem;
  color: var(--d-brown);
  flex: 1;
  min-width: 190px;
}
.autor {
  color: rgba(64, 34, 8, 0.5);
}
.btn-notificar {
  background: #25d366;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}
.btn-notificar:hover:not(:disabled) {
  background: #1da851;
}
.btn-notificar:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Link ───────────────────────────────────────────────── */
.link-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(96, 108, 56, 0.07);
  border-radius: 8px;
  padding: 7px 12px;
  margin-bottom: 1rem;
}
.link-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-green);
  white-space: nowrap;
}
.link-valor {
  flex: 1;
  font-size: 0.72rem;
  color: var(--d-brown);
  word-break: break-all;
}
.btn-copiar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.35);
  border-radius: 6px;
  padding: 2px 8px;
  cursor: pointer;
  color: var(--d-green-dark);
}

/* ── Documentos ─────────────────────────────────────────── */
.seccion-titulo {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--d-green);
  margin-bottom: 8px;
}
.vacio {
  font-size: 0.82rem;
  color: rgba(64, 34, 8, 0.45);
  font-style: italic;
}
.pdf-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 9px;
  padding: 9px 12px;
  margin-bottom: 7px;
}
.pdf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.pdf-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
}
.pdf-nombre {
  font-size: 0.83rem;
  font-weight: 600;
  color: var(--d-brown);
  word-break: break-all;
}
.btn-visualizar {
  background: var(--d-brown);
  color: var(--d-cream);
  border-radius: 7px;
  padding: 3px 11px;
  font-size: 0.76rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}
.btn-visualizar:hover {
  background: var(--d-green-dark);
  color: var(--d-cream);
}

.comentario {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  padding: 8px 10px;
  background: rgba(198, 40, 40, 0.07);
  border-left: 3px solid #c62828;
  border-radius: 0 7px 7px 0;
  font-size: 0.8rem;
  color: #b02020;
}
.respondido,
.respondido-solo {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  opacity: 0.75;
}
.respondido-solo {
  color: rgba(64, 34, 8, 0.5);
}

.modal-footer {
  border-top: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 0.8rem 1.4rem;
}
.btn-cerrar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.4);
  color: var(--d-green);
  font-weight: 600;
  border-radius: 8px;
  font-size: 0.85rem;
}
</style>
